/**
 * @fileoverview Notification Service TypeScript - Sistema notifiche globali
 *
 * Servizio completo per gestire notifiche e messaggi utente attraverso Element UI.
 * Supporta tutti i tipi di notifiche (success, error, warning, info) con
 * configurazione flessibile e API type-safe.
 *
 * @author Generated by TypeScript Migration
 * @since 2025-09-25
 */

import Vue from 'vue';
import type { PluginFunction } from 'vue';

/**
 * Tipi di notifica supportati da Element UI
 */
export type NotificationType = 'success' | 'error' | 'warning' | 'info';

/**
 * Tipi di messaggio supportati da Element UI
 */
export type MessageType = 'success' | 'error' | 'warning' | 'info';

/**
 * Posizioni disponibili per le notifiche
 */
export type NotificationPosition = 'top-right' | 'top-left' | 'bottom-right' | 'bottom-left';

/**
 * Opzioni per notifiche Element UI
 */
export interface NotificationOptions {
	/** Titolo della notifica */
	title?: string;
	/** Messaggio della notifica */
	message: string;
	/** Tipo di notifica */
	type?: NotificationType;
	/** Posizione della notifica */
	position?: NotificationPosition;
	/** Se mostrare il bottone di chiusura */
	showClose?: boolean;
	/** Durata in millisecondi (0 = persistente) */
	duration?: number;
	/** HTML personalizzato */
	dangerouslyUseHTMLString?: boolean;
	/** Callback quando viene chiusa */
	onClose?: () => void;
	/** Callback quando viene cliccata */
	onClick?: () => void;
}

/**
 * Opzioni per messaggi Element UI
 */
export interface MessageOptions {
	/** Messaggio da mostrare */
	message: string;
	/** Tipo di messaggio */
	type?: MessageType;
	/** Se mostrare il bottone di chiusura */
	showClose?: boolean;
	/** Durata in millisecondi (0 = persistente) */
	duration?: number;
	/** HTML personalizzato */
	dangerouslyUseHTMLString?: boolean;
	/** Se centrare il testo */
	center?: boolean;
	/** Callback quando viene chiuso */
	onClose?: () => void;
}

/**
 * Interfaccia per il servizio di notifiche
 */
export interface NotificationService {
	/**
	 * Mostra notifica di errore
	 */
	error(message: string, title?: string, showClose?: boolean): void;

	/**
	 * Mostra messaggio di warning
	 */
	warning(message: string, showClose?: boolean): void;

	/**
	 * Mostra messaggio di successo
	 */
	success(message: string, showClose?: boolean): void;

	/**
	 * Mostra messaggio informativo
	 */
	info(message: string, showClose?: boolean): void;

	/**
	 * Mostra notifica personalizzata
	 */
	notify(options: NotificationOptions): void;

	/**
	 * Mostra messaggio personalizzato
	 */
	message(options: MessageOptions): void;
}

/**
 * Istanza Vue per accedere ai servizi Element UI
 *
 * Utilizziamo un'istanza Vue separata per garantire l'accesso
 * ai servizi $notify e $message di Element UI in qualsiasi contesto.
 */
const vueInstance = new Vue();

/**
 * Crea una notifica utilizzando Element UI $notify
 *
 * @param title - Titolo della notifica
 * @param message - Messaggio da mostrare
 * @param type - Tipo di notifica (default: 'error')
 * @param showClose - Se mostrare bottone chiusura (default: true)
 *
 * @example
 * ```typescript
 * _createNotification('Errore!', 'Operazione fallita', 'error', true);
 * ```
 */
function _createNotification(
	title: string,
	message: string,
	type: NotificationType = 'error',
	showClose: boolean = true,
): void {
	try {
		(vueInstance as any).$notify({
			title,
			message,
			position: 'bottom-right', // Posizione consistente
			showClose,
			type,
		});
	} catch (error) {
		console.error('❌ NotificationService: Failed to create notification:', error);
		// Fallback a console per debugging
		console[type === 'error' ? 'error' : 'log'](`${title}: ${message}`);
	}
}

/**
 * Crea un messaggio utilizzando Element UI $message
 *
 * @param message - Messaggio da mostrare
 * @param type - Tipo di messaggio (default: 'success')
 * @param showClose - Se mostrare bottone chiusura (default: true)
 *
 * @example
 * ```typescript
 * _createMessage('Operazione completata!', 'success', true);
 * ```
 */
function _createMessage(
	message: string,
	type: MessageType = 'success',
	_showClose: boolean = true,
): void {
	try {
		(vueInstance as any).$message({
			message,
			type,
			showClose: true, // Sempre true per consistenza
		});
	} catch (error) {
		console.error('❌ NotificationService: Failed to create message:', error);
		// Fallback a console per debugging
		console[type === 'error' ? 'error' : 'log'](message);
	}
}

/**
 * Implementazione del servizio di notifiche
 *
 * Fornisce metodi semplificati per tutti i tipi di notifiche più comuni,
 * mantenendo la retrocompatibilità con l'API esistente.
 */
const NotificationServiceImpl: NotificationService = {
	/**
	 * Mostra una notifica di errore
	 *
	 * @param message - Messaggio di errore
	 * @param title - Titolo della notifica (default: 'Errore!')
	 * @param showClose - Se mostrare bottone chiusura
	 *
	 * @example
	 * ```typescript
	 * notificationService.error('Salvataggio fallito');
	 * notificationService.error('Connessione persa', 'Errore di rete');
	 * ```
	 */
	error(message: string, title: string = 'Errore!', showClose?: boolean): void {
		_createNotification(title, message, 'error', showClose);
	},

	/**
	 * Mostra un messaggio di warning
	 *
	 * @param message - Messaggio di avvertimento
	 * @param showClose - Se mostrare bottone chiusura
	 *
	 * @example
	 * ```typescript
	 * notificationService.warning('Operazione può richiedere tempo');
	 * ```
	 */
	warning(message: string, showClose?: boolean): void {
		_createMessage(message, 'warning', showClose);
	},

	/**
	 * Mostra un messaggio di successo
	 *
	 * @param message - Messaggio di successo
	 * @param showClose - Se mostrare bottone chiusura
	 *
	 * @example
	 * ```typescript
	 * notificationService.success('Dati salvati correttamente');
	 * ```
	 */
	success(message: string, showClose?: boolean): void {
		_createMessage(message, 'success', showClose);
	},

	/**
	 * Mostra un messaggio informativo
	 *
	 * @param message - Messaggio informativo
	 * @param showClose - Se mostrare bottone chiusura
	 *
	 * @example
	 * ```typescript
	 * notificationService.info('Caricamento in corso...');
	 * ```
	 */
	info(message: string, showClose?: boolean): void {
		_createMessage(message, 'info', showClose);
	},

	/**
	 * Mostra notifica personalizzata con tutte le opzioni
	 *
	 * @param options - Opzioni complete per la notifica
	 *
	 * @example
	 * ```typescript
	 * notificationService.notify({
	 *   title: 'Attenzione',
	 *   message: 'Operazione in corso...',
	 *   type: 'warning',
	 *   position: 'top-right',
	 *   duration: 0, // Persistente
	 * });
	 * ```
	 */
	notify(options: NotificationOptions): void {
		try {
			(vueInstance as any).$notify({
				position: 'bottom-right', // Default position
				showClose: true, // Default show close
				...options, // User options override defaults
			});
		} catch (error) {
			console.error('❌ NotificationService: Failed to create custom notification:', error);
		}
	},

	/**
	 * Mostra messaggio personalizzato con tutte le opzioni
	 *
	 * @param options - Opzioni complete per il messaggio
	 *
	 * @example
	 * ```typescript
	 * notificationService.message({
	 *   message: 'Elaborazione completata',
	 *   type: 'success',
	 *   center: true,
	 *   duration: 3000,
	 * });
	 * ```
	 */
	message(options: MessageOptions): void {
		try {
			(vueInstance as any).$message({
				showClose: true, // Default show close
				...options, // User options override defaults
			});
		} catch (error) {
			console.error('❌ NotificationService: Failed to create custom message:', error);
		}
	},
};

/**
 * Plugin Vue per il servizio di notifiche
 *
 * Installa il servizio globalmente su Vue.prototype,
 * rendendolo accessibile come this.$notificationService.
 */
const NotificationPlugin = {
	/**
	 * Funzione di installazione del plugin Vue
	 *
	 * @param Vue - Costruttore Vue
	 */
	install(Vue: typeof import('vue').default): void {
		try {
			// Registra il servizio notification globalmente
			Vue.prototype.$notificationService = NotificationServiceImpl;
		} catch (error) {
			console.error('❌ Notification Plugin: Failed to install plugin:', error);
			throw error;
		}
	},
} as { install: PluginFunction<any> };

// Export per uso esterno
export default NotificationPlugin;
export { NotificationServiceImpl };

/**
 * Type augmentation per Vue - aggiunge $notificationService al prototype
 *
 * Questo permette a TypeScript di riconoscere this.$notificationService
 * nei componenti Vue senza errori di compilazione.
 */
declare module 'vue/types/vue' {
	interface Vue {
		$notificationService: NotificationService;
	}
}
